import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from 'react-redux';
import Api from 'util/Api';
import api from 'constants/api';
import { assignDriver, getDriverVehiclePlates } from 'services/journeys';
import { authProfile } from 'redux/auth/selectors';

export interface JourneyTemplateItem {
  collectionPointId: number;
  storeName: string;
  marketplaceName?: string;
  address?: string;
  scheduledStart: string;
  estimatedDurationMinutes: number;
}

export interface JourneyTemplate {
  id: number;
  name: string;
  driverId: number | null;
  driverName?: string;
  items: JourneyTemplateItem[];
  createdBy?: string;
  createdAt: string;
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error ?? error?.message ?? fallback;

const KEY = 'journeyTemplates';

const useCenterId = (): number | undefined =>
  useSelector(authProfile)?.paquerypoint?.id;

export const useJourneyTemplates = () => {
  const centerId = useCenterId();
  return useQuery<JourneyTemplate[]>({
    queryKey: [KEY, centerId],
    enabled: !!centerId,
    queryFn: async () => {
      const response = await Api.apiAxios.get(api.journeys.templates, {
        params: { distributionCenterID: centerId },
      });
      if (!Api.isSuccessResponse(response)) {
        throw new Error(
          response.data?.error ?? 'No se pudieron leer las plantillas',
        );
      }
      return response.data?.templates ?? [];
    },
  });
};

export interface JourneyTemplateLog {
  id: number;
  templateId: number;
  templateName: string;
  event: 'created' | 'renamed' | 'deleted' | 'used' | string;
  performedBy?: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export const useJourneyTemplateLogs = (templateId?: number) => {
  const centerId = useCenterId();
  return useQuery<JourneyTemplateLog[]>({
    queryKey: [KEY, 'logs', templateId, centerId],
    enabled: !!templateId && !!centerId,
    queryFn: async () => {
      const response = await Api.apiAxios.get(
        api.journeys.templateLogs(templateId as number),
        { params: { distributionCenterID: centerId } },
      );
      if (!Api.isSuccessResponse(response)) {
        throw new Error(
          response.data?.error ??
            'No se pudo leer el historial de la plantilla',
        );
      }
      return response.data?.logs ?? [];
    },
  });
};

export const useCreateJourneyTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { journeyId: string; name: string }) => {
      const response = await Api.apiAxios.post(api.journeys.templates, payload);
      if (!Api.isSuccessResponse(response)) {
        throw new Error(
          response.data?.error ?? 'No se pudo guardar la plantilla',
        );
      }
      return response.data as JourneyTemplate;
    },
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      message.success(`Se guardó la plantilla "${template.name}".`);
    },
    onError: (error) =>
      message.error(errorMessage(error, 'No se pudo guardar la plantilla')),
  });
};

export const useRenameJourneyTemplate = () => {
  const queryClient = useQueryClient();
  const centerId = useCenterId();
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const response = await Api.apiAxios.put(
        api.journeys.template(id),
        { name },
        { params: { distributionCenterID: centerId } },
      );
      if (!Api.isSuccessResponse(response)) {
        throw new Error(
          response.data?.error ?? 'No se pudo renombrar la plantilla',
        );
      }
      return response.data as JourneyTemplate;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
    onError: (error) =>
      message.error(errorMessage(error, 'No se pudo renombrar la plantilla')),
  });
};

export const useDeleteJourneyTemplate = () => {
  const queryClient = useQueryClient();
  const centerId = useCenterId();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await Api.apiAxios.delete(api.journeys.template(id), {
        params: { distributionCenterID: centerId },
      });
      if (!Api.isSuccessResponse(response)) {
        throw new Error(
          response.data?.error ?? 'No se pudo borrar la plantilla',
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      message.success('Se borró la plantilla.');
    },
    onError: (error) =>
      message.error(errorMessage(error, 'No se pudo borrar la plantilla')),
  });
};

export interface InstantiateResult {
  journeyId: string;
  skipped: { storeName: string; reason: string }[];
}

export interface TemplateAssignment {
  ok: boolean;
  reason?: string;
  activeJourneyId?: string;
  noPlate?: boolean;
}

export const assignTemplateDriver = async (
  journeyId: string,
  driverId: number,
): Promise<TemplateAssignment> => {
  const plates = await getDriverVehiclePlates(driverId).catch(() => []);
  if (!plates[0]) {
    return {
      ok: false,
      noPlate: true,
      reason:
        'no tiene una patente guardada; se asigna desde la ruta, cargándola.',
    };
  }
  try {
    await assignDriver(journeyId, driverId, plates[0]);
    return { ok: true };
  } catch (error) {
    const err = error as { message?: string; activeJourneyId?: string };
    return {
      ok: false,
      reason: err.message ?? 'no se pudo asignar.',
      activeJourneyId: err.activeJourneyId,
    };
  }
};

// El backend arma la ruta entera (nombre y colectas) en una sola llamada. Si
// falla algo del sistema o no entra ninguna colecta, responde 409
// NotCreatedFromTemplate y ya desarmó la ruta: se lanza su mensaje tal cual
// («No se creó la ruta: …»). Las colectas que no entraron vuelven en skipped.
export const instantiateTemplate = async (
  template: JourneyTemplate,
  date: Dayjs,
  centerId: number,
): Promise<InstantiateResult> => {
  const response = await Api.apiAxios.post(
    api.journeys.instantiateTemplate(template.id),
    {
      distributionCenterId: centerId,
      date: date.format('YYYY-MM-DD'),
      utcOffset: dayjs().format('Z'),
    },
  );
  if (!Api.isSuccessResponse(response) || !response.data?.journeyId) {
    throw new Error(response.data?.error ?? 'No se pudo crear la ruta');
  }
  return {
    journeyId: response.data.journeyId,
    skipped: response.data.skipped ?? [],
  };
};
